import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import {
  useBulkUpdateQueueLimits,
  useGetQueueLimits,
} from 'infrastructure/hooks/adherence-settings';

import s from './styles.module.scss';
import SegmentedControl from './segmented-control';
import HourGrid from './hour-grid';
import WindowsView from './windows-view';
import ByDayView from './by-day-view';
import {
  buildMaps,
  cloneLimits,
  collectChanges,
  computeSummary,
  computeTargetKeys,
  isDirty,
  parseLimit,
} from './helpers';

import type { GuidMap, IDayLabel, LimitsMap, QueueLimitsView } from './helpers';

const AdherenceSettingsQueueLimits: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetQueueLimits();
  const { mutateAsync: bulkUpdate, isLoading: saving } =
    useBulkUpdateQueueLimits();

  const [view, setView] = useState<QueueLimitsView>('grid');
  const [limits, setLimits] = useState<LimitsMap>({});
  const [baseline, setBaseline] = useState<LimitsMap>({});
  const [guids, setGuids] = useState<GuidMap>({});
  const [selDays, setSelDays] = useState<Record<number, boolean>>({});
  const [selHours, setSelHours] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [toast, setToast] = useState('');

  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  // Hydrate the editable grid (and the dirty baseline) whenever the server
  // data changes — on first load and after a save-triggered refetch.
  useEffect(() => {
    const maps = buildMaps(data);
    setLimits(maps.limits);
    setGuids(maps.guids);
    setBaseline(cloneLimits(maps.limits));
    setSelDays({});
    setSelHours({});
    setEditing(null);
  }, [data]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const dayLabels: IDayLabel[] = useMemo(
    () => [
      {
        full: t('queueLimits.days.sun'),
        short: t('queueLimits.daysShort.sun'),
      },
      {
        full: t('queueLimits.days.mon'),
        short: t('queueLimits.daysShort.mon'),
      },
      {
        full: t('queueLimits.days.tue'),
        short: t('queueLimits.daysShort.tue'),
      },
      {
        full: t('queueLimits.days.wed'),
        short: t('queueLimits.daysShort.wed'),
      },
      {
        full: t('queueLimits.days.thu'),
        short: t('queueLimits.daysShort.thu'),
      },
      {
        full: t('queueLimits.days.fri'),
        short: t('queueLimits.daysShort.fri'),
      },
      {
        full: t('queueLimits.days.sat'),
        short: t('queueLimits.daysShort.sat'),
      },
    ],
    [t],
  );

  const summary = computeSummary(limits);
  const dirty = isDirty(limits, baseline);
  const targetKeys = useMemo(
    () => new Set(computeTargetKeys(selDays, selHours)),
    [selDays, selHours],
  );

  const flash = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2200);
  };

  const toggleDay = (day: number) =>
    setSelDays((prev) => ({ ...prev, [day]: !prev[day] }));
  const toggleHour = (hour: number) =>
    setSelHours((prev) => ({ ...prev, [hour]: !prev[hour] }));
  const clearSelection = () => {
    setSelDays({});
    setSelHours({});
  };

  const beginEdit = (day: number, hour: number) => {
    setEditing(`${day}-${hour}`);
    setDraft(String(limits[day]?.[hour] ?? ''));
  };

  const commitEdit = () => {
    if (!editing) return;
    const [day, hour] = editing.split('-').map(Number);
    const parsed = parseLimit(draft);
    if (parsed === null) {
      setEditing(null);
      return;
    }
    setLimits((prev) => ({
      ...prev,
      [day]: { ...prev[day], [hour]: parsed },
    }));
    setEditing(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(null);
    }
  };

  const applyBulk = () => {
    const targets = computeTargetKeys(selDays, selHours);
    if (!targets.length) return;
    const parsed = parseLimit(bulkValue);
    if (parsed === null) return;
    setLimits((prev) => {
      const next = cloneLimits(prev);
      targets.forEach((key) => {
        const [day, hour] = key.split('-').map(Number);
        if (!next[day]) next[day] = {};
        next[day][hour] = parsed;
      });
      return next;
    });
    const count = targets.length;
    const noun = count === 1 ? t('queueLimits.hour') : t('queueLimits.hours');
    flash(t('queueLimits.toast.updated', { count, noun }));
  };

  const handleSave = async () => {
    const updates = collectChanges(limits, baseline, guids);
    if (!updates.length) return;
    try {
      await bulkUpdate({ updates });
      setBaseline(cloneLimits(limits));
      flash(t('queueLimits.toast.saved'));
    } catch {
      flash(t('queueLimits.toast.saveError'));
    }
  };

  const handleReset = () => {
    setLimits(cloneLimits(baseline));
    setSelDays({});
    setSelHours({});
    setEditing(null);
    flash(t('queueLimits.toast.reverted'));
  };

  return (
    <div className={s.screen} data-cy="adherence-settings-queue-limits">
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>{t('queueLimits.eyebrow')}</div>
          <h1 className={s.title}>{t('queueLimits.title')}</h1>
          <p className={s.subtitle}>{t('queueLimits.subtitle')}</p>
        </div>
        <div className={s.headerActions}>
          {dirty && (
            <span className={s.pill}>
              <span className={s.pillDot} />
              {t('queueLimits.unsavedChanges')}
            </span>
          )}
          <BaseButton
            type="secondary"
            dataCy="queue-limits-reset"
            label={t('queueLimits.reset')}
            onClick={handleReset}
            disabled={saving}
          />
          <BaseButton
            type="primary"
            dataCy="queue-limits-save"
            label={t('queueLimits.saveChanges')}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty || saving}
          />
        </div>
      </div>

      <div className={s.toolbar}>
        <SegmentedControl view={view} onChange={setView} />
        <div className={s.summary}>
          {t('queueLimits.summary', {
            peak: summary.peak.toLocaleString(),
            weekTotal: summary.weekTotal.toLocaleString(),
          })}
        </div>
      </div>

      <div className={s.body}>
        {isLoading ? (
          <div className={s.loading}>
            <Spin />
          </div>
        ) : (
          <>
            {view === 'grid' && (
              <HourGrid
                limits={limits}
                dayLabels={dayLabels}
                selDays={selDays}
                selHours={selHours}
                targetKeys={targetKeys}
                editing={editing}
                draft={draft}
                bulkValue={bulkValue}
                onToggleDay={toggleDay}
                onToggleHour={toggleHour}
                onBeginEdit={beginEdit}
                onDraftChange={setDraft}
                onCommit={commitEdit}
                onKeyDown={onKeyDown}
                onBulkValueChange={setBulkValue}
                onApply={applyBulk}
                onClearSelection={clearSelection}
              />
            )}
            {view === 'table' && (
              <WindowsView limits={limits} dayLabels={dayLabels} />
            )}
            {view === 'day' && (
              <ByDayView limits={limits} dayLabels={dayLabels} />
            )}
          </>
        )}
      </div>

      {!!toast && <div className={s.toast}>{toast}</div>}
    </div>
  );
};

export default AdherenceSettingsQueueLimits;
